import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  Camera, Plus, Image, Edit2, Trash2, Eye, EyeOff, X, 
  LogOut, Upload, ExternalLink, Instagram, Globe, User,
  Calendar, FolderOpen, ChevronRight
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const PhotographerPortal = () => {
  const navigate = useNavigate();
  const [photographer, setPhotographer] = useState(null);
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("galleries");
  
  // Gallery form state
  const [showGalleryForm, setShowGalleryForm] = useState(false);
  const [editingGallery, setEditingGallery] = useState(null);
  const [galleryForm, setGalleryForm] = useState({
    title: "",
    category: "",
    event_name: "",
    event_date: "",
    description: "",
    cover_image_url: "",
    is_visible: true
  });
  
  // Photo upload state
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [newPhotoUrl, setNewPhotoUrl] = useState("");
  const [newPhotoCaption, setNewPhotoCaption] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadMode, setUploadMode] = useState("file"); // "file" or "url"

  const getAuthHeader = () => ({
    Authorization: `Bearer ${localStorage.getItem("photographer_token")}`
  });

  useEffect(() => {
    const token = localStorage.getItem("photographer_token");
    if (!token) {
      navigate("/photographer/login");
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [profileRes, galleriesRes] = await Promise.all([
        axios.get(`${API}/photographers/me`, { headers: getAuthHeader() }),
        axios.get(`${API}/photographers/me/galleries`, { headers: getAuthHeader() })
      ]);
      setPhotographer(profileRes.data);
      setGalleries(galleriesRes.data || []);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("photographer_token");
        localStorage.removeItem("photographer_user");
        navigate("/photographer/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("photographer_token");
    localStorage.removeItem("photographer_user");
    navigate("/photographer/login");
  };

  const handleGallerySubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingGallery) {
        await axios.put(
          `${API}/photographers/me/galleries/${editingGallery.id}`,
          galleryForm,
          { headers: getAuthHeader() }
        );
      } else {
        await axios.post(`${API}/photographers/me/galleries`, galleryForm, {
          headers: getAuthHeader()
        });
      }
      fetchData();
      closeGalleryForm();
    } catch (err) {
      alert(err.response?.data?.detail || "Error saving gallery");
    }
  };

  const handleDeleteGallery = async (galleryId) => {
    if (!window.confirm("Delete this gallery and all its photos?")) return;
    try {
      await axios.delete(`${API}/photographers/me/galleries/${galleryId}`, {
        headers: getAuthHeader()
      });
      fetchData();
      if (selectedGallery?.id === galleryId) setSelectedGallery(null);
    } catch (err) {
      alert("Error deleting gallery");
    }
  };

  const handleToggleVisibility = async (gallery) => {
    try {
      await axios.put(
        `${API}/photographers/me/galleries/${gallery.id}`,
        { is_visible: !gallery.is_visible },
        { headers: getAuthHeader() }
      );
      fetchData();
    } catch (err) {
      alert("Error updating gallery");
    }
  };

  const handleAddPhoto = async (e) => {
    e.preventDefault();
    if (!selectedGallery || !newPhotoUrl) return;
    
    setUploadingPhoto(true);
    try {
      await axios.post(
        `${API}/photographers/me/galleries/${selectedGallery.id}/photos`,
        { url: newPhotoUrl, caption: newPhotoCaption },
        { headers: getAuthHeader() }
      );
      
      // Refresh selected gallery
      const res = await axios.get(
        `${API}/photographers/me/galleries/${selectedGallery.id}`,
        { headers: getAuthHeader() }
      );
      setSelectedGallery(res.data);
      fetchData();
      setNewPhotoUrl("");
      setNewPhotoCaption("");
    } catch (err) {
      alert("Error adding photo");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    // Filter for images and limit to 10
    const validFiles = files.filter(f => f.type.startsWith("image/")).slice(0, 10);
    setSelectedFiles(validFiles);
  };

  const handleFileUpload = async () => {
    if (!selectedGallery || selectedFiles.length === 0) return;
    
    setUploadingPhoto(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append("files", file);
      });
      
      await axios.post(
        `${API}/photographers/me/galleries/${selectedGallery.id}/upload`,
        formData,
        {
          headers: {
            ...getAuthHeader(),
            "Content-Type": "multipart/form-data"
          },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percent);
          }
        }
      );
      
      // Refresh selected gallery
      const res = await axios.get(
        `${API}/photographers/me/galleries/${selectedGallery.id}`,
        { headers: getAuthHeader() }
      );
      setSelectedGallery(res.data);
      fetchData();
      setSelectedFiles([]);
      setUploadProgress(0);
      
      // Reset file input
      const fileInput = document.getElementById("file-upload-input");
      if (fileInput) fileInput.value = "";
      
    } catch (err) {
      alert(err.response?.data?.detail || "Error uploading photos");
    } finally {
      setUploadingPhoto(false);
      setUploadProgress(0);
    }
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDeletePhoto = async (photoId) => {
    if (!selectedGallery) return;
    try {
      await axios.delete(
        `${API}/photographers/me/galleries/${selectedGallery.id}/photos/${photoId}`,
        { headers: getAuthHeader() }
      );
      
      // Refresh selected gallery
      const res = await axios.get(
        `${API}/photographers/me/galleries/${selectedGallery.id}`,
        { headers: getAuthHeader() }
      );
      setSelectedGallery(res.data);
      fetchData();
    } catch (err) {
      alert("Error removing photo");
    }
  };

  const openEditGallery = (gallery) => {
    setEditingGallery(gallery);
    setGalleryForm({
      title: gallery.title || "",
      category: gallery.category || "",
      event_name: gallery.event_name || "",
      event_date: gallery.event_date || "",
      description: gallery.description || "",
      cover_image_url: gallery.cover_image_url || "",
      is_visible: gallery.is_visible ?? true
    });
    setShowGalleryForm(true);
  };

  const closeGalleryForm = () => {
    setShowGalleryForm(false);
    setEditingGallery(null);
    setGalleryForm({
      title: "",
      category: "",
      event_name: "",
      event_date: "",
      description: "",
      cover_image_url: "",
      is_visible: true
    });
  };

  const categories = ["Football", "Basketball", "Baseball", "Soccer", "Volleyball", "Track", "Tennis", "Swimming", "Wrestling", "Cheerleading", "Other"];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="photographer-portal">
      {/* Header */}
      <header className="bg-black border-b border-zinc-800 sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Camera className="w-8 h-8 text-orange-500" />
            <div>
              <h1 className="text-white font-bold">Photographer Portal</h1>
              <p className="text-zinc-500 text-xs">{photographer?.name}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <Link to="/" className="text-zinc-500 hover:text-white text-sm">
              View Site
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-zinc-500 hover:text-white text-sm"
            >
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-zinc-900/50 border border-white/5 p-6">
            <div className="text-3xl font-bold text-white">{galleries.length}</div>
            <div className="text-zinc-500 text-sm">Galleries</div>
          </div>
          <div className="bg-zinc-900/50 border border-white/5 p-6">
            <div className="text-3xl font-bold text-white">{photographer?.total_photos || 0}</div>
            <div className="text-zinc-500 text-sm">Total Photos</div>
          </div>
          <div className="bg-zinc-900/50 border border-white/5 p-6">
            <div className="text-3xl font-bold text-white">
              {galleries.filter(g => g.is_visible).length}
            </div>
            <div className="text-zinc-500 text-sm">Published</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 border-b border-zinc-800 mb-6 overflow-x-auto scrollbar-hide">
          <button
            onClick={() => { setActiveTab("galleries"); setSelectedGallery(null); }}
            className={`px-4 py-3 text-sm font-bold uppercase ${activeTab === "galleries" ? 'text-orange-500 border-b-2 border-orange-500' : 'text-zinc-500 hover:text-white'}`}
          >
            <FolderOpen className="w-4 h-4 inline mr-2" />
            My Galleries
          </button>
          <button
            onClick={() => setActiveTab("profile")}
            className={`px-4 py-3 text-sm font-bold uppercase ${activeTab === "profile" ? 'text-orange-500 border-b-2 border-orange-500' : 'text-zinc-500 hover:text-white'}`}
          >
            <User className="w-4 h-4 inline mr-2" />
            Profile
          </button>
        </div>

        {/* Galleries Tab */}
        {activeTab === "galleries" && !selectedGallery && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">My Galleries</h2>
              <button
                onClick={() => setShowGalleryForm(true)}
                className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-black font-bold hover:bg-orange-400"
              >
                <Plus className="w-4 h-4" /> New Gallery
              </button>
            </div>

            {galleries.length === 0 ? (
              <div className="text-center py-16 bg-zinc-900/30 border border-white/5">
                <Image className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                <h3 className="text-white text-lg mb-2">No Galleries Yet</h3>
                <p className="text-zinc-500 text-sm mb-4">Create your first gallery to start uploading photos</p>
                <button
                  onClick={() => setShowGalleryForm(true)}
                  className="px-4 py-2 bg-orange-500 text-black font-bold hover:bg-orange-400"
                >
                  Create Gallery
                </button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {galleries.map((gallery) => (
                  <div
                    key={gallery.id}
                    className={`bg-zinc-900/50 border overflow-hidden ${gallery.is_visible ? 'border-white/10' : 'border-red-500/30'}`}
                  >
                    {/* Cover */}
                    <div 
                      className="aspect-video bg-zinc-800 relative cursor-pointer group"
                      onClick={() => setSelectedGallery(gallery)}
                    >
                      {gallery.cover_image_url ? (
                        <img
                          src={gallery.cover_image_url}
                          alt={gallery.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Image className="w-12 h-12 text-zinc-700" />
                        </div>
                      )}
                      
                      {!gallery.is_visible && (
                        <div className="absolute top-2 left-2 px-2 py-1 bg-red-500/90 text-white text-xs font-bold">
                          Hidden
                        </div>
                      )}
                      
                      <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/70 text-white text-xs">
                        {gallery.images?.length || 0} photos
                      </div>

                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="text-white font-bold flex items-center gap-1">
                          Manage <ChevronRight className="w-4 h-4" />
                        </span>
                      </div>
                    </div>

                    {/* Info */}
                    <div className="p-4">
                      {gallery.category && (
                        <span className="text-orange-500 text-xs font-bold uppercase">{gallery.category}</span>
                      )}
                      <h3 className="text-white font-bold mt-1 line-clamp-1">{gallery.title}</h3>
                      {gallery.event_name && (
                        <p className="text-zinc-500 text-xs mt-1">{gallery.event_name}</p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="border-t border-white/5 p-3 flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleToggleVisibility(gallery)}
                        className={`p-2 ${gallery.is_visible ? 'text-green-500' : 'text-red-500'}`}
                        title={gallery.is_visible ? "Hide" : "Show"}
                      >
                        {gallery.is_visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => openEditGallery(gallery)}
                        className="p-2 text-zinc-500 hover:text-white"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteGallery(gallery.id)}
                        className="p-2 text-zinc-500 hover:text-red-500"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Selected Gallery - Photo Management */}
        {activeTab === "galleries" && selectedGallery && (
          <div>
            <button
              onClick={() => setSelectedGallery(null)}
              className="text-zinc-500 hover:text-white mb-4 text-sm"
            >
              ← Back to Galleries
            </button>

            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-white">{selectedGallery.title}</h2>
                {selectedGallery.event_name && (
                  <p className="text-zinc-500">{selectedGallery.event_name}</p>
                )}
              </div>
              <Link
                to={`/photos/${selectedGallery.id}`}
                target="_blank"
                className="text-orange-500 hover:text-orange-400 flex items-center gap-1 text-sm"
              >
                View Public Page <ExternalLink className="w-3 h-3" />
              </Link>
            </div>

            {/* Add Photos Section */}
            <div className="bg-zinc-900/50 border border-white/5 p-4 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-bold">Add Photos</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => setUploadMode("file")}
                    className={`px-3 py-1.5 text-xs font-bold uppercase ${uploadMode === "file" ? "bg-orange-500 text-black" : "bg-zinc-800 text-zinc-400 hover:text-white"}`}
                  >
                    <Upload className="w-3 h-3 inline mr-1" /> Upload Files
                  </button>
                  <button
                    onClick={() => setUploadMode("url")}
                    className={`px-3 py-1.5 text-xs font-bold uppercase ${uploadMode === "url" ? "bg-orange-500 text-black" : "bg-zinc-800 text-zinc-400 hover:text-white"}`}
                  >
                    <ExternalLink className="w-3 h-3 inline mr-1" /> From URL
                  </button>
                </div>
              </div>

              {uploadMode === "file" ? (
                <div>
                  {/* File Upload Zone */}
                  <div className="border-2 border-dashed border-white/20 hover:border-orange-500/50 transition-colors p-6 text-center mb-4">
                    <input
                      id="file-upload-input"
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <label 
                      htmlFor="file-upload-input"
                      className="cursor-pointer block"
                    >
                      <Upload className="w-10 h-10 text-zinc-500 mx-auto mb-3" />
                      <p className="text-white mb-1">Click to select photos</p>
                      <p className="text-zinc-500 text-sm">or drag and drop (max 10 files, 10MB each)</p>
                      <p className="text-zinc-600 text-xs mt-2">Supported: JPEG, PNG, WebP, GIF</p>
                    </label>
                  </div>

                  {/* Selected Files Preview */}
                  {selectedFiles.length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-zinc-400 text-sm">{selectedFiles.length} file(s) selected</span>
                        <button
                          onClick={() => setSelectedFiles([])}
                          className="text-zinc-500 hover:text-white text-xs"
                        >
                          Clear All
                        </button>
                      </div>
                      <div className="grid grid-cols-5 gap-2">
                        {selectedFiles.map((file, idx) => (
                          <div key={idx} className="relative group">
                            <img
                              src={URL.createObjectURL(file)}
                              alt={file.name}
                              className="aspect-square object-cover border border-white/10"
                            />
                            <button
                              onClick={() => removeSelectedFile(idx)}
                              className="absolute top-1 right-1 p-1 bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-3 h-3" />
                            </button>
                            <p className="text-zinc-500 text-[10px] truncate mt-1">{file.name}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Upload Progress */}
                  {uploadingPhoto && uploadProgress > 0 && (
                    <div className="mb-4">
                      <div className="flex justify-between text-xs text-zinc-400 mb-1">
                        <span>Uploading...</span>
                        <span>{uploadProgress}%</span>
                      </div>
                      <div className="h-2 bg-zinc-800 overflow-hidden">
                        <div 
                          className="h-full bg-orange-500 transition-all"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Upload Button */}
                  <button
                    onClick={handleFileUpload}
                    disabled={uploadingPhoto || selectedFiles.length === 0}
                    className="w-full px-6 py-3 bg-orange-500 text-black font-bold hover:bg-orange-400 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {uploadingPhoto ? "Uploading..." : `Upload ${selectedFiles.length} Photo${selectedFiles.length !== 1 ? "s" : ""}`}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleAddPhoto} className="flex gap-4">
                  <input
                    type="url"
                    value={newPhotoUrl}
                    onChange={(e) => setNewPhotoUrl(e.target.value)}
                    placeholder="Image URL (https://...)"
                    required
                    className="flex-1 px-4 py-2 bg-black border border-white/10 text-white focus:border-orange-500 focus:outline-none"
                  />
                  <input
                    type="text"
                    value={newPhotoCaption}
                    onChange={(e) => setNewPhotoCaption(e.target.value)}
                    placeholder="Caption (optional)"
                    className="w-48 px-4 py-2 bg-black border border-white/10 text-white focus:border-orange-500 focus:outline-none"
                  />
                  <button
                    type="submit"
                    disabled={uploadingPhoto}
                    className="px-6 py-2 bg-orange-500 text-black font-bold hover:bg-orange-400 disabled:opacity-50"
                  >
                    {uploadingPhoto ? "Adding..." : "Add Photo"}
                  </button>
                </form>
              )}
            </div>

            {/* Photo Grid */}
            {selectedGallery.images?.length === 0 ? (
              <div className="text-center py-12 bg-zinc-900/30 border border-white/5">
                <Upload className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-500">No photos yet. Add your first photo above.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {selectedGallery.images?.map((photo, idx) => (
                  <div key={photo.id || idx} className="group relative aspect-square bg-zinc-800">
                    <img
                      src={photo.url}
                      alt={photo.caption || `Photo ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() => handleDeletePhoto(photo.id)}
                      className="absolute top-2 right-2 p-1.5 bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Delete photo"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    {photo.caption && (
                      <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
                        <p className="text-white text-xs truncate">{photo.caption}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === "profile" && (
          <div className="max-w-xl">
            <h2 className="text-xl font-bold text-white mb-6">My Profile</h2>
            
            <div className="bg-zinc-900/50 border border-white/5 p-6 space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-full bg-zinc-800 flex items-center justify-center">
                  {photographer?.profile_photo_url ? (
                    <img
                      src={photographer.profile_photo_url}
                      alt={photographer.name}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <User className="w-8 h-8 text-zinc-600" />
                  )}
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg">{photographer?.name}</h3>
                  <p className="text-zinc-500">@{photographer?.username}</p>
                </div>
              </div>

              {photographer?.bio && (
                <p className="text-zinc-400 text-sm">{photographer.bio}</p>
              )}

              <div className="pt-4 border-t border-white/5 space-y-2">
                {photographer?.website && (
                  <a
                    href={photographer.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm"
                  >
                    <Globe className="w-4 h-4" /> {photographer.website}
                  </a>
                )}
                {photographer?.instagram && (
                  <a
                    href={`https://instagram.com/${photographer.instagram.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm"
                  >
                    <Instagram className="w-4 h-4" /> {photographer.instagram}
                  </a>
                )}
              </div>

              <div className="pt-4 border-t border-white/5">
                <p className="text-zinc-600 text-xs">
                  <Calendar className="w-3 h-3 inline mr-1" />
                  Member since {new Date(photographer?.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Gallery Form Modal */}
      {showGalleryForm && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-zinc-900 border border-white/10 w-full max-w-lg">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-xl font-bold text-white">
                {editingGallery ? "Edit Gallery" : "New Gallery"}
              </h3>
              <button onClick={closeGalleryForm} className="text-zinc-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleGallerySubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Gallery Title *</label>
                <input
                  type="text"
                  required
                  value={galleryForm.title}
                  onChange={(e) => setGalleryForm({ ...galleryForm, title: e.target.value })}
                  className="w-full px-4 py-3 bg-black border border-white/10 text-white focus:border-orange-500 focus:outline-none"
                  placeholder="e.g. Championship Game Photos"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Category</label>
                  <select
                    value={galleryForm.category}
                    onChange={(e) => setGalleryForm({ ...galleryForm, category: e.target.value })}
                    className="w-full px-4 py-3 bg-black border border-white/10 text-white focus:border-orange-500 focus:outline-none"
                  >
                    <option value="">Select...</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Event Date</label>
                  <input
                    type="date"
                    value={galleryForm.event_date}
                    onChange={(e) => setGalleryForm({ ...galleryForm, event_date: e.target.value })}
                    className="w-full px-4 py-3 bg-black border border-white/10 text-white focus:border-orange-500 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-1">Event Name</label>
                <input
                  type="text"
                  value={galleryForm.event_name}
                  onChange={(e) => setGalleryForm({ ...galleryForm, event_name: e.target.value })}
                  className="w-full px-4 py-3 bg-black border border-white/10 text-white focus:border-orange-500 focus:outline-none"
                  placeholder="e.g. State Finals vs. Eagles"
                />
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-1">Description</label>
                <textarea
                  value={galleryForm.description}
                  onChange={(e) => setGalleryForm({ ...galleryForm, description: e.target.value })}
                  className="w-full px-4 py-3 bg-black border border-white/10 text-white focus:border-orange-500 focus:outline-none h-20 resize-none"
                />
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-1">Cover Image URL</label>
                <input
                  type="url"
                  value={galleryForm.cover_image_url}
                  onChange={(e) => setGalleryForm({ ...galleryForm, cover_image_url: e.target.value })}
                  className="w-full px-4 py-3 bg-black border border-white/10 text-white focus:border-orange-500 focus:outline-none"
                  placeholder="https://..."
                />
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_visible"
                  checked={galleryForm.is_visible}
                  onChange={(e) => setGalleryForm({ ...galleryForm, is_visible: e.target.checked })}
                  className="w-5 h-5"
                />
                <label htmlFor="is_visible" className="text-white">
                  Publish gallery (visible on site)
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={closeGalleryForm}
                  className="px-6 py-2 border border-white/20 text-white hover:bg-white/5"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-orange-500 text-black font-bold hover:bg-orange-400"
                >
                  {editingGallery ? "Update" : "Create"} Gallery
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotographerPortal;
